import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import Pane from 'components/Common/Pane'
import config from 'constants/config'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'

import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import LineGiftIcon from 'components/Luxkit/Icons/line/LineGiftIcon'
import FormatCurrency from 'components/Common/FormatCurrency'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import { OfferPackageOptionView, getCheapestSelectedPackageOptionWithDefault, areCalendarsLoading, isBNBLAvailable } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Pill from 'components/Luxkit/Pill'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import InlineGroup from 'components/Common/InlineGroup'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import { getWeeklyInstalmentAmount, isInstalmentsAvailableOnOffer } from 'lib/payment/payInInstalmentsUtils'
import SecureWithFlexiblePaymentsPriceDetails from 'components/Common/SecureWithFlexiblePayments/SecureWithFlexiblePaymentsPriceDetails'
import GiftTooltip from 'components/Common/Gift/GiftTooltip'
import { getInstalmentsAbTestEnabled } from 'selectors/featuresSelectors'
import { reserveForZeroEnabled } from 'checkout/selectors/featureConfig/reserveForZeroSelector'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import Group from 'components/utils/Group'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import OfferFromDurationLabel from '../OfferFromDurationLabel'
import HotelPlusFlightsFrom from 'components/Common/HotelPlusFlightsFrom'

const StyledOfferPartnershipLabel = styled(OfferPartnershipLabel)`
  margin-top: ${rem(12)};
`

const SummaryPane = styled(Pane)`
  padding: ${rem(28)} ${rem(20)} ;
  position: relative;
  border: 1px solid ${({ theme }) => theme.palette.neutral.six};
  border-radius: ${props => props.theme.borderRadius.XS};c

  > * + * {
    margin-top: ${rem(12)};
  }
`

const PackagePrices = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(4)};
  align-items: center;
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(8)};
  text-align: center;
`

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const TaxAndFeesWrapper = styled.div`
  display: flex;
  align-items: center;
`

interface Props {
  offer: App.Offer;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms: Array<App.Occupants>;
  className?: string;
  cheapestSelectedOption: OfferPackageOptionView;
  onViewPackageOptions: () => void;
  calendarsLoading?: boolean;
  checkIn: moment.Moment;
  isBNBLAvailable: boolean;
  instalmentsABTestEnabled: boolean;
  isReserveForZeroEnabled: boolean;
  defaultDepositAmountPercentage: number;
}

function FlashOfferSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    flightOrigin,
    cheapestSelectedOption: packageView,
    rooms,
    flightDestination,
    calendarsLoading,
    checkIn,
    isBNBLAvailable,
    instalmentsABTestEnabled,
    isReserveForZeroEnabled,
    defaultDepositAmountPercentage,
  } = props

  const { currentCurrency, showTaxExclusivePrices, currentRegionCode } = useContext(GeoContext)
  const dispatch = useOfferPageDispatch()

  const {
    flightPrice,
    hotelValue,
    hotelPrice,
    package: pkg,
    hotelDiscountPercent,
    bundledDiscountPercent,
    taxesAndFees,
    propertyFees,
  } = packageView

  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const availableForPurchase = useIsAvailableForPurchase()
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const onToggleFilter = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }

  const departureTooFarInFuture = useMemo(() => checkIn && checkIn.diff(moment.utc(), 'days') > 330, [checkIn])
  const hideNoCacheMessaging = !hasFlights && offer.bundledWithFlightsOnly
  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + (propertyFees ?? 0)
  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const showHotelPrice = !offer.bundledWithFlightsOnly || (offer.bundledWithFlightsOnly && flightsEnabled && !hideNoCacheMessaging && hasFlights)
  const showDiscountPill = (shouldShowDiscountPercent && !offer.bundledWithFlightsOnly) || (offer.bundledWithFlightsOnly && shouldShowBundledDiscountPercent)

  const showPriceDetails = taxesAndFees > 0 || (propertyFees ?? 0) > 0

  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, checkIn, totalPrice)
  const showPayInInstalments = instalmentsABTestEnabled && isInstalmentsAvailableOnOffer(offer, currentCurrency, checkIn)
  const isReserveForZeroTagShown = isReserveForZeroEnabled && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkIn)
  const isDepositTagShown = showSecureWithDepositTag || showPayInInstalments
  const instalmentWeeklyAmount = getWeeklyInstalmentAmount(hotelPrice, checkIn, offer, defaultDepositAmountPercentage)
  const shouldShowValue = hotelValue > hotelPrice && pkg.shouldDisplayValue
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const isIndiaRegion = currentRegionCode === 'IN'

  const bookByFormatted = useMemo(() => {
    return moment(offer.bookByDate)
  }, [offer.bookByDate])

  const travelUntilFormatted = useMemo(() => {
    return moment(offer.travelToDate)
  }, [offer.travelToDate])

  const { partnership } = useContext(GeoContext)

  const viewOptionsLabel = `View ${config.brandConfig.travelSynonym} options`

  return (
    <SummaryPane type="clean" shadow className={className} data-testid="SummaryPane">
      <Group direction="vertical" gap={16}>
        {availableForPurchase && <>
          {showPrices &&
            <PackagePrices>
              {isTimeLimited(offer) && <OfferTimeLimitedText />}
              {isDepositTagShown && !isReserveForZeroEnabled && <SecureWithDepositTagAndTooltip offerType={offer.type} />}
              {isReserveForZeroTagShown && <ReserveForZeroTag />}
              <PriceDetails>
                {offer.bundledWithFlightsOnly &&
                  <CaptionTextBlock variant="large" weight="bold" startIcon={<LinePlaneFlyIcon />}>
                    Hotel + flights{hideNoCacheMessaging && ' packages available'}
                  </CaptionTextBlock>
                }
                {showHotelPrice && <>
                  <OfferFromDurationLabel variant="small" wrap="no-wrap" />
                  <InlineGroup vertical="center" data-testid="pricing">
                    <Heading variant="heading3" as="span" colour={exclusivePriceColor} >
                      <FormatCurrency value={offer.bundledWithFlightsOnly ? totalPrice + flightPrice : totalPrice} format="roundedDollar" />
                    </Heading>
                    <BodyText as="span" variant="large" weight="semi-bold">/{offer.bundledWithFlightsOnly ? 'package*' : offer.saleUnit}</BodyText>
                    {offer.bundledWithFlightsOnly && !rooms &&
                      <Caption variant="medium" colour="neutral-three">
                      &nbsp;Two Adults
                      </Caption>
                  }
                  </InlineGroup>
                  {(showPayInInstalments && !!instalmentWeeklyAmount) && <SecureWithFlexiblePaymentsPriceDetails currency={currentCurrency} isOfferSummary depositPercentage={depositAmountPercentage} instalmentAmount={instalmentWeeklyAmount} />}
                  <TaxAndFeesWrapper>
                    <TaxesAndFees
                      taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
                      taxAmount={taxesAndFees}
                    />
                    {showPriceDetails && <PriceDetailsModal
                      offerPage
                      offer={offer}
                      duration={pkg.duration}
                      taxesAndFees={taxesAndFees}
                      propertyFees={propertyFees}
                      hotelPrice={hotelPrice}
                      flightPrice={offer.bundledWithFlightsOnly ? flightPrice : undefined}
                      rooms={rooms}
                      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                      label="infoButton"
                      labelSize="small"
                      cta={
                        <TextButton
                          kind="primary"
                          fit="flex"
                        >
                          {viewOptionsLabel}
                        </TextButton>
                      }
                      onCtaAfterClose={onViewPackageOptions}
                    />}
                  </TaxAndFeesWrapper>
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue &&
                    <ValueWrapper variant="medium" weight="bold">
                      <span data-testid="value-box">
                        <Caption variant="large" weight="bold">
                          Valued up to <FormatCurrency value={offer.bundledWithFlightsOnly ? hotelValue + flightPrice : hotelValue} />
                        </Caption>
                      </span>
                      {showDiscountPill &&
                        <Pill kind="primary" variant="success">
                          Save up to {offer.bundledWithFlightsOnly ? bundledDiscountPercent : hotelDiscountPercent }%
                        </Pill>
                    }
                    </ValueWrapper>
                }
                </>}
              </PriceDetails>
              { partnership && partnership.code !== 'velocity' && <StyledOfferPartnershipLabel offer={offer} />}
              <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
              {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                type="estimate"
                offer={offer}
                totalCost={totalPrice}
                numberOfNights={pkg.duration}
              />}
            </PackagePrices>}
          <TextButton
            kind="primary"
            data-testid="OfferAvailable"
            onClick={onViewPackageOptions}
            fit="flex"
          >
            {viewOptionsLabel}
          </TextButton>
          <Group direction="vertical">
            {flightsEnabled && !hideNoCacheMessaging && <PriceDetails>
              {!offer.bundledWithFlightsOnly && hasFlights && (
                <HotelPlusFlightsFrom price={totalPrice + flightPrice} forTwoAdults={!rooms} priceColour={exclusivePriceColor} data-testid="FlightPrice" />
              )}
              {!hasFlights && flightOrigin?.code === flightDestination?.code &&
                <ChangeDepartureWrapper>
                  <Caption variant="large" colour="neutral-three">
                    No flights available from {flightOrigin.name}. Please select another airport.
                  </Caption>
                  <TextLink onClick={onToggleFilter} data-filter="airport">
                    Change departure airport
                  </TextLink>
                </ChangeDepartureWrapper>
              }
              {!hasFlights && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture &&
                <Caption variant="large" colour="neutral-three" align="center">
                  {!isIndiaRegion && 'Add flights to the hotel package. Select from all major airlines at the next step.'}
                  {isIndiaRegion && 'Add flights to your hotel package from the best available airlines.'}
                </Caption>
              }
              {!hasFlights && departureTooFarInFuture &&
                <ChangeDepartureWrapper>
                  <Caption variant="large" colour="neutral-three">
                    Flights can only be booked up to 11 months in advance.
                  </Caption>
                </ChangeDepartureWrapper>
              }
              <AtolProtectedTooltip />
            </PriceDetails>}
            {isBNBLAvailable && <InlineGroup horizontal="center" vertical="center">
              <LineGiftIcon />&nbsp;
              <Caption variant="medium">Gifting available&nbsp;&nbsp;</Caption>
              <GiftTooltip dates={{ bookBy: bookByFormatted, travelUntil: travelUntilFormatted }}>
                <Caption underline variant="medium">
                  Learn more
                </Caption>
              </GiftTooltip>
            </InlineGroup>}
          </Group>
        </>}
      </Group>
      {!availableForPurchase &&
        <>
          <BodyText
            variant="large"
            weight="bold"
            data-testid="OfferSoldOut"
          >
            This offer has SOLD OUT
          </BodyText>
          <TextButton
            kind="primary"
            to={`/${OFFER_TYPE_HOTEL_SLUG}`}
            fit="flex"
          >
            Browse all hotel offers
          </TextButton>
        </>
      }
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  rooms: state.rooms,
  offer: state.offer,
  checkIn: state.checkInDate,
  flightOrigin: state.airport,
  calendarsLoading: areCalendarsLoading(appState, state),
  isBNBLAvailable: isBNBLAvailable(state),
  instalmentsABTestEnabled: getInstalmentsAbTestEnabled(appState),
  isReserveForZeroEnabled: reserveForZeroEnabled(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(FlashOfferSummary)
